import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiUploadCloud, FiFileText, FiImage, FiX } from "react-icons/fi";
import toast from "react-hot-toast";

const BulkUpload = ({
  bulkModal,
  setBulkModal,
  excelLoading,
  zipLoading,
  bulkUploadExcel,
  getValues,
  showBulkErrors,
  bulkUploadImages,
}) => {
  const isLoading = excelLoading || zipLoading;

  return (
    <AnimatePresence>
      {bulkModal && (
        <motion.div
          className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center px-3 sm:px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            initial={{ scale: 0.92, opacity: 0, y: 30 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.92, opacity: 0, y: 30 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="
              bg-white rounded-2xl w-full
              max-w-[95vw] sm:max-w-md
              shadow-2xl overflow-hidden
            "
          >
            {/* ---------- Header ---------- */}
            <div className="flex items-center justify-between px-4 sm:px-5 py-3 sm:py-4 border-b bg-slate-50">
              <div className="flex items-center gap-2">
                {bulkModal === "excel" ? (
                  <FiFileText className="text-indigo-600 text-base sm:text-lg" />
                ) : (
                  <FiImage className="text-emerald-600 text-base sm:text-lg" />
                )}
                <h2 className="text-sm sm:text-base font-semibold text-slate-800">
                  {bulkModal === "excel"
                    ? "Bulk Stock Upload (Excel)"
                    : "Bulk Image Upload (ZIP)"}
                </h2>
              </div>

              <button
                onClick={() => setBulkModal(null)}
                disabled={isLoading}
                className="p-1.5 rounded-md hover:bg-slate-200 disabled:opacity-60"
              >
                <FiX className="text-slate-500 text-lg" />
              </button>
            </div>

            {/* ---------- Content ---------- */}
            <div className="p-4 sm:p-5 space-y-4">
              {/* Info Box */}
              <div className="rounded-lg border bg-slate-50 p-3 text-[11px] sm:text-xs text-slate-600 leading-relaxed">
                {bulkModal === "excel" ? (
                  <>
                    • Upload <b>.xlsx / .xls</b> template only <br />
                    • Category, Product, Design, Purity must exactly match masters <br />
                    • Item Code will be auto-generated
                  </>
                ) : (
                  <>
                    • Upload a <b>.zip</b> file containing images <br />
                    • Image name must start with <b>ItemCode</b> <br />
                    • Max 5 images per item
                  </>
                )}
              </div>

              {/* Upload Box */}
              <label className="group cursor-pointer block">
                <div
                  className="
                    border-2 border-dashed rounded-xl
                    p-5 sm:p-6
                    text-center transition
                    group-hover:border-indigo-500
                    active:scale-[0.98]
                  "
                >
                  <FiUploadCloud className="mx-auto text-2xl sm:text-3xl text-slate-400 group-hover:text-indigo-600" />
                  <p className="mt-2 text-sm font-medium text-slate-700">
                    Tap / Click to select file
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    {bulkModal === "excel"
                      ? "Excel file (.xlsx)"
                      : "ZIP file (.zip)"}
                  </p>
                </div>

                <input
                  type="file"
                  accept={bulkModal === "excel" ? ".xlsx,.xls" : ".zip"}
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;

                    try {
                      if (bulkModal === "excel") {
                        await bulkUploadExcel({
                          file,
                          vendorId: getValues("vendor").value,
                          branchId: getValues("branch").value,
                          counterId: getValues("counter").value,
                        }).unwrap();

                        toast.success("Excel uploaded successfully");
                      } else {
                        await bulkUploadImages(file).unwrap();
                        toast.success("Images uploaded successfully");
                      }

                      setBulkModal(null);
                    } catch (err) {
                      setBulkModal(null);
                      showBulkErrors(err);
                    } finally {
                      e.target.value = "";
                    }
                  }}
                />
              </label>

              {/* Loader */}
              {isLoading && (
                <div className="flex items-center justify-center gap-3 text-xs sm:text-sm text-slate-600 pt-2">
                  <span className="h-4 w-4 sm:h-5 sm:w-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                  Uploading… please wait
                </div>
              )}
            </div>

            {/* ---------- Footer ---------- */}
            <div className="flex justify-end px-4 sm:px-5 py-3 border-t bg-slate-50">
              <button
                onClick={() => setBulkModal(null)}
                disabled={isLoading}
                className="
                  px-4 py-2
                  text-xs sm:text-sm
                  rounded-md
                  bg-slate-200 hover:bg-slate-300
                  disabled:opacity-60
                "
              >
                Cancel
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BulkUpload;
